import { createContext } from 'react';
import { APIError } from '../../services/api';
import {array} from "yup";

export interface Operator {
    data: OperatorData;
    errors: APIError;
    event_id: number;
    flow: string;
}

export interface OperatorData {
    name: string;
    handle: string;
    custom_domain: string | null;
    allow_premium_card_acceptance: boolean;
    allow_stripe_test: boolean;
    opt_in_message: string | null;
    is_opt_in_default: boolean;
    country: string;
    ccy: string;
    locale: string;
    has_active_promos: boolean;
    theme: Theme;
    channels: { collection: boolean; delivery: boolean; dine_in: boolean; catering: boolean; venues: boolean };
    flow?: string;
    meta_tag?: string;
    google_analytics?: string;
    zillionaire?: ZillionaireData;
}

export interface ZillionaireData {
    payment: string;
    confirmation: string;
    promo: string|null;
}

export interface Theme {
    v4: Theme4;
}

export interface Theme4 {
    favicon: string | null;
    logo: string | null;
    hero: string | null;
    hero_2: string | null;
    bg_tile: string | null;

    primary_font_name: string | null;
    primary_font_spacing: string | null;
    primary_font_case: string | null;
    primary_font_regular: string | null;
    primary_font_bold: string | null;
    primary_font_italic: string | null;
    primary_font_bold_italic: string | null;
    secondary_font_name: string | null;
    secondary_font_spacing: string | null;
    secondary_font_case: string | null;
    secondary_font_regular: string | null;
    secondary_font_bold: string | null;
    secondary_font_italic: string | null;
    secondary_font_bold_italic: string | null;

    navigation_style: string;
    card_style: string;
    card_shadow: string;

    xl_radius: string;
    l_radius: string;
    m_radius: string;
    s_radius: string;

    layout_1: string;
    layout_2: string;
    layout_3: string;
    layout_4: string;
    layout_5: string;
    layout_6: string;
    layout_7: string
    layout_8: string
    layout_9: string
    layout_2_border: string;
    layout_2_border_width: string;
    layout_3_border: string;
    layout_3_border_width: string;
    layout_4_type: string;
    layout_5_type: string;
    layout_6_type: string;
    layout_7_type: string;
    layout_8_border: string;
    layout_9_type: string;
    ui_1: string;
    ui_2: string;
    ui_3: string;
    ui_4: string;
    ui_5: string;
    ui_6: string;
    ui_7: string;
    ui_8: string;
    ui_9: string;
    ui_10: string;
    ui_11: string;
    ui_12: string;
    ui_13: string;
    ui_14: string;
    ui_15: string;
    ui_16: string;
    ui_17: string;
    ui_18: string;
    ui_19: string;
    ui_20: string;
    ui_21: string;
    ui_22: string;
    ui_23: string;
    card_border: string;
    navigation_border: string;
    underline_border: string;
    primary_cta_border: string;
    text_xl_header: string;
    text_l_header: string;
    text_m_header: string;
    text_s_header: string;
    text_l_body: string;
    text_m_body: string;

    cookie_banner: boolean;
    allergens_advice: boolean;
    allergens_advice_onsite: boolean;
    preferences_modal: boolean;
    location_search: boolean;
    address_autocomplete_delivery: boolean;
    address_autocomplete_catering: boolean;
    customer_notes: boolean;
    upsell: boolean;
    tipping_collection: boolean;
    tipping_delivery: boolean;
    tipping_catering: boolean;
    tipping_venues: boolean;
    tipping_dine_in: boolean;
    remember_me: boolean;
    remember_me_default: boolean;

    slogan: string | null;
    allergen_info: string | null;
    allergen_info_onsite: string | null;
    cookie_banner_text: string | null;
    opt_in: string | null;
    opt_in_default: boolean;
    catering_label: string | null;

    custom_css: string | null;
}

export interface Errors {}

export type TOperatorSite = {
    id: number;
    uid: string;
    operator_id: number;
    name: string;
    site_handle: string;
    address_1: string;
    is_coming_soon: number; // should be boolean
    delivery_is_open: boolean;
    delivery_area_type: string; // find out types
    delivery_area_radius: number;
    delivery_area_postcodes: string | null;
    delivery_partner: string | null; // find out
    delivery_no_fee_threshold: number;
    delivery_fee: number;
    delivery_min_order: number;
    delivery_max_order: number;
    collection_min_order: number;
    collection_max_order: number;
    collection_is_open: boolean;
    lat: number;
    lng: number;
    collection_banner: string | null;
    delivery_banner: string | null;
    see_collection: number; // should be boolean
    see_delivery: number; // should be boolean
    is_off_list: number; // should be boolean
    collection_instructions_closed: string | null;
    delivery_instructions_closed: string | null;
    collection_instructions: string | null;
    collection_instructions_show_at_payment: number; // should be boolean
    delivery_instructions: string | null;
    delivery_instructions_show_at_payment: number; // should be boolean
    see_dine_in: number; // should be boolean
    dine_in_is_open: boolean;
    dine_in_instructions: string | null;
    dine_in_instructions_closed: string | null;
    dine_in_banner: string | null;
    dine_in_instructions_show_at_payment: number; // should be boolean
    dine_in_instructions_show_at_confirmation: number; // should be boolean
    dine_in_service_fee_type: string; // find types
    dine_in_service_fee_amount: string; // why is this string
    hasCollectionSlots: boolean;
    hasDeliverySlots: boolean;
    hasDineInSlots: boolean;
    closures: any[];
};

export type TChannelInPerson = 'collection' | 'dine_in';

export type TChannel = 'delivery' | TChannelInPerson | 'catering' | 'venues';

export type OperatorContextData = {
    operator: OperatorData | null;
    setOperator: (operator: OperatorData | null) => void;

    fetchOperator: (handle: string) => void;

    loading: boolean;
    setLoading: (state: boolean) => void;
};

export const OperatorContext = createContext<OperatorContextData>({
    operator: null,
    setOperator: () => {},

    fetchOperator: () => {},

    loading: false,
    setLoading: () => {},
});
